// +----------------------------------------------------------------------
// | EB [ EB赋能开发者，助力企业发展 ]
// +----------------------------------------------------------------------
// | Copyright (c) 2016-2026 https://www.eb.com All rights reserved.
// +----------------------------------------------------------------------
// | Licensed EB并不是自由软件，未经许可不能去掉EB相关版权
// +----------------------------------------------------------------------
// | Author: EB Team <admin@eb.com>
// +----------------------------------------------------------------------
/**
 * 商户 user 模块（Pinia）
 * 对应 PHP/Vue2: EB view/mer/src/store/modules/user.js
 */
import { login as loginApi, logout as logoutApi, getMenusApi, getBaseInfo } from '@/api/user';
import { getToken, setToken, removeToken } from '@/utils/auth';
import router from '@/router';
import Cookies from 'js-cookie';
import { formatFlatteningRoutes } from '@/utils/system';
import { defineStore } from 'pinia';
import useCrmebMenuStore from '@/store/modules/menu';
import useTagsViewStore from '@/store/modules/tagsView';
import { computeVenueFeatures, normalizeVenueMode, type MerchantVenueModeFeatures, type VenueModeValue } from '@/utils/residentVenueMenu';

const ONE_LV_ROUTES_KEY = 'oneLvRoutes';

export interface UserState {
  token?: string;
  name: string;
  avatar: string;
  introduction: string;
  marginStatus: number;
  marginDays: string;
  roles: string[];
  sidebarWidth: string | null;
  sidebarStyle: string | null;
  oneLvMenus: unknown[];
  oneLvRoutes: unknown[];
  childMenuList: unknown[];
  /** EB access 权限码（按钮级，来自 mer/info.access） */
  crmebAccess: string[];
  /** EB 管理员 level：0 主账号，>0 子账号 */
  crmebAdminLevel: number;
  /** 驻场场所模式 · spec §4 */
  venueMode: VenueModeValue;
  venueFeatures: MerchantVenueModeFeatures;
}

function readOneLvRoutes(): unknown[] {
  try {
    const raw = localStorage.getItem(ONE_LV_ROUTES_KEY);
    if (raw) {
      return JSON.parse(raw) as unknown[];
    }
  } catch {
    // ignore
  }
  return [];
}

const useUserStore = defineStore('user', {
  state: (): UserState => ({
    token: getToken(),
    name: '',
    avatar: '',
    introduction: '',
    marginStatus: 0,
    marginDays: '',
    roles: [],
    sidebarWidth: window.localStorage.getItem('sidebarWidth'),
    sidebarStyle: window.localStorage.getItem('sidebarStyle'),
    oneLvMenus: [],
    oneLvRoutes: readOneLvRoutes(),
    childMenuList: [],
    crmebAccess: [],
    crmebAdminLevel: 0,
    venueMode: 'eventHost',
    venueFeatures: computeVenueFeatures('eventHost'),
  }),
  getters: {
    marginRemindStatus: (state) => !!state.marginStatus,
  },
  actions: {
    /**
     * 用户登录
     * 对应 EB: user/login
     */
    login(userInfo: Record<string, unknown>) {
      return loginApi(userInfo).then((response: any) => {
        const payload = (response?.data ?? response) as Record<string, unknown>;
        const data = (payload?.data ?? payload) as Record<string, unknown>;
        const flatRoutes = router.getRoutes().map((item) => ({ ...item }));
        const oneLvMenus = formatFlatteningRoutes(flatRoutes as unknown[]) || [];
        const loginRoutes = Array.isArray(response.data)
          ? formatFlatteningRoutes(response.data as unknown[]) || []
          : Array.isArray(payload)
            ? formatFlatteningRoutes(payload as unknown[]) || []
            : [];
        try {
          localStorage.setItem(ONE_LV_ROUTES_KEY, JSON.stringify(loginRoutes));
        } catch {
          // ignore
        }
        this.oneLvMenus = oneLvMenus;
        this.oneLvRoutes = loginRoutes;
        const token = String(data.token ?? '');
        this.token = token;
        setToken(token);
        if (data.admin) {
          try {
            localStorage.setItem('MerAdminInfo', JSON.stringify(data.admin));
          } catch {
            // ignore
          }
        }
        this.roles = ['crmeb'];
        return data;
      });
    },

    /**
     * 拉取侧栏菜单
     * 对应 EB: user/getMenus
     */
    getMenus() {
      const crmebMenu = useCrmebMenuStore();
      return getMenusApi().then((response: any) => {
        const payload = response?.data ?? response;
        const tree = (Array.isArray(payload) ? payload : (payload as Record<string, unknown>)?.data ?? payload) as unknown[];
        crmebMenu.setMenuTree(Array.isArray(tree) ? tree : []);
        return response;
      });
    },

    /**
     * 拉取 mer/info（店铺与管理员信息）
     */
    loadProfile() {
      return getBaseInfo()
        .then((res: any) => {
          const info = (res?.data ?? res) as Record<string, unknown> | null;
          if (!info) {
            return info;
          }
          if (info.marginRemindStatus !== undefined) {
            this.marginStatus = info.marginRemindStatus ? 1 : 0;
          } else if (info.marginStatus !== undefined) {
            this.marginStatus = Number(info.marginStatus) || 0;
          }
          if (info.marginDays !== undefined) {
            this.marginDays = String(info.marginDays ?? '');
          }
          const merAccount = info.merAccount;
          const merAdmin = (info.merAdminInfo ??
            info.admin ??
            (merAccount && typeof merAccount === 'object' ? merAccount : null)) as Record<
            string,
            unknown
          > | null;
          if (merAdmin) {
            const displayName = merAdmin.realName ?? merAdmin.account;
            if (displayName) {
              this.name = String(displayName);
            }
            if (merAdmin.avatar) {
              this.avatar = String(merAdmin.avatar);
            }
            if (merAdmin.level !== undefined && merAdmin.level !== null) {
              this.crmebAdminLevel = Number(merAdmin.level);
            }
          }
          const access = info.access ?? [];
          if (Array.isArray(access)) {
            this.crmebAccess = access as string[];
            useCrmebMenuStore().setAccess(this.crmebAccess);
          }
          this.applyVenueModeSettings(info);
          this.roles = ['crmeb'];
          return info;
        })
        .catch(() => null);
    },

    /** 应用驻场模式（mer/info 或 resident/venue-mode/settings） */
    applyVenueModeSettings(payload: Record<string, unknown>) {
      const raw = String(payload.venueMode ?? 'eventHost');
      const mode = normalizeVenueMode(raw);
      const featuresRaw = payload.venueFeatures;
      this.venueMode = mode;
      this.venueFeatures =
        featuresRaw && typeof featuresRaw === 'object'
          ? { ...computeVenueFeatures(mode), ...(featuresRaw as MerchantVenueModeFeatures) }
          : computeVenueFeatures(mode);
      useCrmebMenuStore().refreshVenueMenuFilter();
    },

    /** 登录后会话初始化：店铺信息（含 venueMode）→ 菜单 */
    initCrmebSession() {
      return this.loadProfile()
        .then(() => this.getMenus())
        .then(() => {
          this.roles = ['crmeb'];
          return true;
        });
    },

    setMarginStatus(status: number) {
      this.marginStatus = status;
    },

    setMarginDays(days: string) {
      this.marginDays = days;
    },

    /** 首页保证金提醒：布尔开关写入 marginStatus */
    setMarginRemindStatus(status: boolean) {
      this.marginStatus = status ? 1 : 0;
    },

    setOneLvMenus(oneLvMenus: unknown[]) {
      this.oneLvMenus = oneLvMenus;
    },

    setOneLvRoute(oneLvRoutes: unknown[]) {
      this.oneLvRoutes = oneLvRoutes;
    },

    setChildMenuList(list: unknown[]) {
      this.childMenuList = list;
    },

    setSidebarWidth(width: string) {
      this.sidebarWidth = width;
    },

    setSidebarStyle(style: string) {
      this.sidebarStyle = style;
      window.localStorage.setItem('sidebarStyle', style);
    },

    /**
     * 退出登录
     * 对应 EB: user/logout
     */
    logout() {
      return logoutApi()
        .then(() => this.clearSession())
        .catch(() => this.clearSession());
    },

    clearSession() {
      this.token = '';
      this.roles = [];
      this.name = '';
      this.avatar = '';
      this.introduction = '';
      this.marginStatus = 0;
      this.marginDays = '';
      this.crmebAccess = [];
      this.crmebAdminLevel = 0;
      this.oneLvMenus = [];
      this.oneLvRoutes = [];
      this.childMenuList = [];
      removeToken();
      useCrmebMenuStore().clear();
      useTagsViewStore().delAllViews();
      Cookies.remove('merchantToken');
      Cookies.remove('MerName');
      Cookies.remove('MerInfo');
      try {
        localStorage.removeItem('MerAdminInfo');
        localStorage.removeItem(ONE_LV_ROUTES_KEY);
      } catch {
        // ignore
      }
    },

    /**
     * 清除 token
     * 对应 EB: user/resetToken
     */
    resetToken() {
      this.token = '';
      this.roles = [];
      removeToken();
    },
  },
});

export default useUserStore;
